import { useContext } from 'react';
import { Link } from 'react-router-dom';
import { Images } from '@urbanx/agx-ui-components';
import { useGetAgentsInAgency } from '@urbanx/agx.agencies.client';
import { AuthContext } from '@urbanx/agx.auth.client';
import {
  ActionMenu,
  AgxAvatar,
  AgxSpacer,
  AgxTypography,
  Icon_Flip2Outline,
  Icon_PersonOutline,
  type ActionMenuItem,
} from '@urbanx/agx.ui-components2';
import { useFeatureToggles } from 'hooks/useFeatureToggles';
import useLogout from 'hooks/useLogout';

type ProfileMenuAction = 'profile' | 'integrations' | 'logout';

const ProfileMenu = () => {
  const { userAccount } = useContext(AuthContext);
  const { agentProfileSettings, isLoading } = useFeatureToggles();
  const { data: agents } = useGetAgentsInAgency();
  const logout = useLogout();
  const agent = agents?.find(({ id }) => id === userAccount.agent);
  const firstName = agent?.name?.firstName ?? userAccount.firstName;
  const lastName = agent?.name?.lastName ?? userAccount.lastName;
  const initials = `${firstName?.[0] ?? ''}${lastName?.[0] ?? ''}`.toUpperCase();

  if (isLoading || !agentProfileSettings) return null;

  const actions: ActionMenuItem<ProfileMenuAction>[] = [
    {
      action: 'profile',
      to: '/settings/profile',
      children: (
        <AgxSpacer direction="horizontal" gap="spacing-sm" contentAlignment="center">
          <AgxAvatar
            className="profileMenu__itemIcon"
            type="icon-light"
            size="xs"
            icon={<Icon_PersonOutline />}
          />
          <AgxTypography
            id="profile-menu-profile-label"
            type="body_ui"
            size="small"
            color="inherit"
          >
            Profile
          </AgxTypography>
        </AgxSpacer>
      ),
    },
    {
      action: 'integrations',
      to: '/settings/integrations',
      children: (
        <AgxSpacer direction="horizontal" gap="spacing-sm" contentAlignment="center">
          <AgxAvatar
            className="profileMenu__itemIcon"
            type="icon-light"
            size="xs"
            icon={<Icon_Flip2Outline />}
          />
          <AgxTypography
            id="profile-menu-integrations-label"
            type="body_ui"
            size="small"
            color="inherit"
          >
            Integrations
          </AgxTypography>
        </AgxSpacer>
      ),
    },
    {
      action: 'logout',
      onClick: logout,
      children: (
        <AgxSpacer direction="horizontal" gap="spacing-sm" contentAlignment="center">
          <AgxAvatar
            className="profileMenu__itemIcon"
            type="icon-light"
            size="xs"
            icon={<Images.Logout />}
          />
          <AgxTypography id="profile-menu-logout-label" type="body_ui" size="small" color="inherit">
            Logout
          </AgxTypography>
        </AgxSpacer>
      ),
    },
  ];

  return (
    <ActionMenu
      id="profile-menu"
      actions={actions}
      dataTestId="profile-menu"
      className="profileMenu"
      menuClassName="profileMenu__menu"
      triggerAriaLabel="Open profile menu"
      trigger={
        agent?.photoUrl ? (
          <AgxAvatar
            className="profileMenu__avatar"
            type="image"
            size="md"
            imageUri={agent.photoUrl}
          />
        ) : (
          <AgxAvatar className="profileMenu__avatar" type="text-dark" size="md" text={initials} />
        )
      }
      renderLink={({ to, children, ...props }) => (
        <Link to={to} {...props}>
          {children}
        </Link>
      )}
    />
  );
};

export default ProfileMenu;
